import { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Card,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  Stack,
  Tooltip,
} from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudIcon from '@mui/icons-material/Cloud';
import AddRoadOutlinedIcon from '@mui/icons-material/AddRoadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import { styled } from '@mui/material/styles';
import {
  PrimaryButton,
  CancelButton,
  flexAlignCenterGap,
  CenteredTypography,
} from '../../../utils/common/styles/commonStyles';
import {
  selectAllDrives,
  selectDrivesStatus,
  selectedDriveVideoURL,
  driveVideoUrlLoading,
  deleteDriveLoading,
} from '../store/drive.selectors';
import {
  setDriveID,
  fetchDrivesByCollection,
  fetchDrivesByCompany,
  deleteDrive,
  fetchSelectedDriveStorageUrl,
  clearDrivesAction,
} from '../store/drive.actions';
import { reprocessDriveApi } from '../api/drive.api';
import { editDriveName } from '../../dashboard/store/dashboard.action';
import DriveMapComponent from './DriveMapComponent';
import { selectAuthUser } from '../../auth/store/auth.selectors';
import MasterMapComponent from '../components/MasterMapComponent';
import DriveSidebar from './Sidebar';
import ManageCollectionModal from '../../collection/components/ManageCollectionModal';
import Breadcrumbs from '../../layout/Breadcrumbs';

const Wrapper = styled(Box)(({ theme }) => ({
  maxWidth: '100%',
  padding: theme.spacing(0.6, 2),
  boxSizing: 'border-box',
}));

const GridLayout = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'block',
  width: '100%',
  overflow: 'hidden',
}));

const MainGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gridTemplateRows: 'auto 1fr',
  gap: theme.spacing(1.5),
  minHeight: '720px',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto auto auto',
    gap: theme.spacing(2),
    minHeight: 'auto',
  },
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
  },
}));

const MapColumn = styled(Paper)(({ theme }) => ({
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: '650px',
  maxHeight: 'calc(100vh - 170px)',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius * 2,
  position: 'relative',
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
  [theme.breakpoints.down('md')]: {
    maxHeight: '450px',
    minHeight: '450px',
  },
}));

const MapColumnMaster = styled(Paper)(({ theme }) => ({
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: '650px',
  maxHeight: 'calc(100vh - 170px)',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius * 2,
  position: 'relative',
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
  [theme.breakpoints.down('md')]: {
    maxHeight: 'none',
  },
}));

const DetailsColumn = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  height: '100%',
  maxHeight: '100%',
  overflowY: 'auto',
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: '#fafafa',
  border: '1px solid #e2e8f0',
  boxShadow: 'none',
  boxSizing: 'border-box',
  minWidth: 0,
  width: '100%',
}));

const InfoCard = ({ title, children, sx }) => (
  <Card sx={sx}>
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        mb: 1.5,
        color: '#1e293b',
        backgroundColor: '#f1f5f9',
        p: 1,
        borderRadius: 1,
      }}
    >
      {title}
    </Typography>
    {children}
  </Card>
);

InfoCard.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

InfoCard.defaultProps = {
  sx: {},
};

const Drive = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const drives = useSelector(selectAllDrives) || [];
  const drivesStatus = useSelector(selectDrivesStatus);
  const user = useSelector(selectAuthUser);

  const query = new URLSearchParams(location.search);
  const viewAll = query.get('viewAll') === 'true';

  const [collectionId, setCollectionId] = useState(null);
  const [restoredCollection, setRestoredCollection] = useState(null);
  const [selectedDrive, setSelectedDrive] = useState(null);

  const currentCollectionId =
    collectionId ||
    selectedDrive?.collectionID ||
    selectedDrive?.collectionId ||
    selectedDrive?.collection_id ||
    null;

  const currentCollectionName =
    restoredCollection?.name ||
    restoredCollection?.collectionName ||
    selectedDrive?.collectionName ||
    selectedDrive?.collection_name ||
    '';
  const [search, setSearch] = useState('');

  const driveVideoUrl = useSelector(selectedDriveVideoURL);
  const isDriveVideoLoading = useSelector(driveVideoUrlLoading);
  const deleteDriveStatus = useSelector(deleteDriveLoading);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openDelete, setOpenDelete] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [newDriveName, setNewDriveName] = useState('');
  const [openReprocess, setOpenReprocess] = useState(false);
  const [openVisit, setOpenVisit] = useState(false);
  const [manageCollectionOpen, setManageCollectionOpen] = useState(false);
  const [driveVideo, setDriveVideo] = useState(null);
  const videoRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const NonAllowdedRoles = user?.rolename?.includes('Rieker') || user?.rolename === 'Manager';

  const isEditNameUnchanged = Boolean(
    selectedDrive && newDriveName.trim() === selectedDrive.name?.trim()
  );
  const isEditConfirmDisabled = !newDriveName.trim() || isEditNameUnchanged;
  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Collections', path: '/collections' },
    { label: 'Drives', path: '/drives' },
  ];
  const currentRoleId = Number(
    user?.roleID ||
      user?.roleId ||
      user?.roleid ||
      JSON.parse(localStorage.getItem('user') || '{}')?.roleid
  );
  const shouldShowBreadcrumbs = [1, 2, 3].includes(currentRoleId);
  useEffect(() => {
    dispatch(clearDrivesAction());
    setSelectedDrive(null);
  }, [dispatch]);

  useEffect(() => {
    if (viewAll) {
      dispatch(clearDrivesAction());
      setSelectedDrive(null);
      if (user && user.companyid) {
        dispatch(fetchDrivesByCompany(user.companyid));
      }
      return;
    }

    if (collectionId) {
      dispatch(clearDrivesAction());
      setSelectedDrive(null);
      dispatch(fetchDrivesByCollection(collectionId));
    }
  }, [collectionId, viewAll, dispatch, user]);

  const companyData = localStorage.getItem('CompanyData');
  const companydata = companyData ? JSON.parse(companyData) : null;

  useEffect(() => {
    if (viewAll) return;

    try {
      const stored = localStorage.getItem('createDriveFromCollection');
      if (!stored) return;

      const parsed = JSON.parse(stored);
      if (parsed && parsed.id && parsed.id !== collectionId) {
        setCollectionId(parsed.id);
        setRestoredCollection(parsed);
      }
    } catch (e) {
      // Error restoring collection
    }
  }, [collectionId, viewAll]);

  useEffect(() => {
    if (!selectedDrive) return;
    if (selectedDrive.id) {
      dispatch(setDriveID(selectedDrive.id));
    }
  }, [selectedDrive, dispatch]);

  useEffect(() => {
    if (drives.length > 0 && !selectedDrive) {
      try {
        const stored = localStorage.getItem('selectedDriveRow');
        if (stored) {
          const parsed = JSON.parse(stored);
          const found = drives.find((d) => d.id === parsed.id);
          setDriveVideo(parsed.video_url || null);
          if (found) setSelectedDrive(found);
        }
      } catch (e) {
        // ignore
      }
    }
  }, [drives, selectedDrive, driveVideo]);

  useEffect(() => {
    if (selectedDrive && drives.length > 0) {
      const updatedDrive = drives.find((d) => d.id === selectedDrive.id);
      if (updatedDrive && updatedDrive.name !== selectedDrive.name) {
        setSelectedDrive(updatedDrive);
      }
    }
  }, [drives, selectedDrive]);

  useEffect(() => {
    if (openEdit && selectedDrive) {
      setNewDriveName(selectedDrive.name || '');
    }
  }, [openEdit, selectedDrive]);

  const showDrives = viewAll || Boolean(collectionId);

  const handleDriveSelect = (drive) => {
    setSelectedDrive(drive);
    setSidebarOpen(false);
  };

  const combinedDrives = useMemo(() => {
    if (!showDrives || drivesStatus === 'loading') {
      return [];
    }

    let filtered = drives;
    if (search && drives.length > 0) {
      filtered = drives.filter((d) => d.name?.toLowerCase().includes(search.toLowerCase()));
    }

    return filtered;
  }, [drives, search, drivesStatus, showDrives]);

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const calcDuration = (start, end) => {
    if (!start || !end) return '—';
    const diff = new Date(end).getTime() - new Date(start).getTime();
    const mins = Math.round(diff / 60000);
    return `${mins} mins`;
  };

  const handleEditConfirm = async () => {
    if (!newDriveName.trim()) return;
    try {
      const resp = await dispatch(editDriveName(selectedDrive.id, newDriveName.trim()));
      if (resp?.status === 'success') {
        setSnackbar({
          open: true,
          message: 'Drive name updated.',
          severity: 'success',
        });
        setSelectedDrive((prev) => (prev ? { ...prev, name: newDriveName.trim() } : prev));
        dispatch(fetchDrivesByCollection(collectionId));
        setOpenEdit(false);
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to update drive name.',
          severity: 'error',
        });
      }
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.message || 'Error updating name',
        severity: 'error',
      });
    }
  };

  const handleReprocessConfirm = async () => {
    try {
      const response = await reprocessDriveApi(selectedDrive.id);
      setSnackbar({
        open: true,
        message: response.data?.message || 'Reprocess triggered',
        severity: response.data?.status === 'success' ? 'success' : 'error',
      });
      dispatch(fetchDrivesByCollection(collectionId));
      setOpenReprocess(false);
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.message || 'Failed to reprocess',
        severity: 'error',
      });
    }
  };

  const handleVisitConfirm = async () => {
    try {
      const storageUrl = await dispatch(fetchSelectedDriveStorageUrl(selectedDrive.id));
      if (storageUrl?.data) {
        window.open(storageUrl.data);
        setOpenVisit(false);
      }
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.message || 'Failed to fetch storage URL',
        severity: 'error',
      });
    }
  };

  return (
    <Wrapper>
      {/* Top Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {shouldShowBreadcrumbs && <Breadcrumbs items={breadcrumbItems} />}
        </Box>
        <CenteredTypography
          variant="h5"
          sx={{ fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}
        >
          {viewAll ? 'Company Drives' : restoredCollection?.name || 'Collection Drives'}
        </CenteredTypography>
        <Box sx={{ width: 180 }} />
      </Box>

      <GridLayout>
        {/* Responsive Sliding Drive Sidebar */}
        <Box
          sx={{
            position: 'absolute',
            top: 60,
            bottom: 12,
            left: 12,
            width: { xs: 290, sm: 330 },
            zIndex: 25,
            transform: sidebarOpen ? 'translateX(0)' : 'translateX(calc(-100% - 14px))',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            pointerEvents: 'none',
          }}
        >
          {/* Main Sidebar Card */}
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'background.paper',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              borderRadius: '16px',
              boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.2)',
              overflow: 'hidden',
              pointerEvents: sidebarOpen ? 'auto' : 'none',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.75,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                sx={{
                  color: 'black',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  lineHeight: 1.2,
                }}
              >
                Drive List
              </Typography>
            </Box>

            <DriveSidebar
              drives={combinedDrives}
              viewAll={viewAll}
              isLoading={drivesStatus === 'loading'}
              search={search}
              setSearch={setSearch}
              navigate={navigate}
              setSelectedDrive={handleDriveSelect}
              selectedDrive={selectedDrive}
              onManageCollection={() => {
                if (currentCollectionId) {
                  setManageCollectionOpen(true);
                } else {
                  setSnackbar({
                    open: true,
                    message: 'Please select a collection before managing.',
                    severity: 'warning',
                  });
                }
              }}
            />
          </Box>

          {/* Sidebar Toggle Button */}
          <IconButton
            onClick={() => setSidebarOpen((isOpen) => !isOpen)}
            aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '100%',
              transform: 'translateY(-50%)',
              width: 30,
              height: 150,
              border: '1px solid #cbd5e1',
              borderLeft: 0,
              borderRadius: '0 10px 10px 0',
              color: '#334155',
              backgroundColor: '#ffffff',
              boxShadow: '4px 0 10px rgba(15, 23, 42, 0.12)',
              pointerEvents: 'auto',
              p: 0,
              '&:hover': {
                backgroundColor: '#f8fafc',
              },
            }}
          >
            {sidebarOpen ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
          </IconButton>
        </Box>

        {/* Main Workspace (50/50 split) */}
        <MainGrid
          sx={{
            gridTemplateColumns: selectedDrive
              ? { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }
              : '1fr',
          }}
        >
          {/* Header Action Bar */}
          <Box
            sx={{
              gridColumn: '1 / -1',
              borderRadius: 2,
              backgroundColor: 'background.paper',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              borderBottom: '3px solid #6366f1',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
                padding: '8px 16px',
                columnGap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }} />

              <Typography
                variant="h6"
                sx={{
                  color: '#0f172a',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  justifySelf: 'center',
                }}
              >
                {viewAll
                  ? selectedDrive?.name || 'Company Drives Map'
                  : selectedDrive?.name || 'Collection Drives Map'}
              </Typography>

              {selectedDrive ? (
                <Box sx={{ ...flexAlignCenterGap(0.5), justifyContent: 'flex-end', minWidth: 0 }}>
                  <Tooltip title="Edit drive name">
                    <IconButton
                      size="medium"
                      onClick={() => {
                        if (!selectedDrive || !selectedDrive.id) return;
                        setNewDriveName(selectedDrive.name || '');
                        setOpenEdit(true);
                      }}
                    >
                      <EditIcon fontSize="small" sx={{ color: '#475569' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete drive">
                    <IconButton size="medium" onClick={() => setOpenDelete(true)}>
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reprocess drive">
                    <IconButton size="medium" onClick={() => setOpenReprocess(true)}>
                      <RefreshIcon fontSize="small" color="success" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cloud storage">
                    <IconButton
                      size="medium"
                      disabled={!NonAllowdedRoles}
                      onClick={() => setOpenVisit(true)}
                    >
                      <CloudIcon fontSize="small" color="action" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Box sx={{ minWidth: 0 }} />
              )}
            </Paper>

            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                severity={snackbar.severity}
                variant="filled"
                sx={{ width: '100%', fontWeight: 500 }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>

            <Dialog open={openDelete} onClose={() => setOpenDelete(false)} fullWidth maxWidth="sm">
              <Box sx={{ px: 3, py: 2.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Delete Drive
                </Typography>
                <Typography sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
                  This action cannot be undone. Are you sure you want to delete this drive?
                </Typography>
                <Box sx={{ ...flexAlignCenterGap(2), justifyContent: 'flex-end' }}>
                  {deleteDriveStatus ? (
                    <CircularProgress size={24} />
                  ) : (
                    <>
                      <CancelButton onClick={() => setOpenDelete(false)}>Cancel</CancelButton>
                      <PrimaryButton
                        onClick={async () => {
                          if (!selectedDrive || !selectedDrive.id) return;
                          const response = await dispatch(deleteDrive(selectedDrive.id));
                          if (response?.status === 'success') {
                            setSnackbar({
                              open: true,
                              message: 'Drive deleted successfully.',
                              severity: 'success',
                            });
                            setOpenDelete(false);
                            dispatch(fetchDrivesByCollection(collectionId));
                            setSelectedDrive(null);
                          } else {
                            setSnackbar({
                              open: true,
                              message: 'Failed to delete drive. Please try again.',
                              severity: 'error',
                            });
                          }
                        }}
                        variant="contained"
                      >
                        Delete
                      </PrimaryButton>
                    </>
                  )}
                </Box>
              </Box>
            </Dialog>
          </Box>

          {/* Left Column: Map View (50% Width) */}
          {selectedDrive ? (
            <MapColumn elevation={0}>
              <Box sx={{ width: '100%', height: '100%', flex: 1 }}>
                <DriveMapComponent
                  driveId={selectedDrive.id}
                  setSnackbar={setSnackbar}
                  videoRef={videoRef}
                />
              </Box>
            </MapColumn>
          ) : (
            <MapColumnMaster elevation={0} sx={{ gridColumn: '1 / -1', height: '100%' }}>
              <Box sx={{ width: '100%', height: '100%', flex: 1 }}>
                <MasterMapComponent
                  driveId={null}
                  setSnackbar={setSnackbar}
                  setSelectedDrive={setSelectedDrive}
                  isLoading={drivesStatus === 'loading'}
                />
              </Box>
            </MapColumnMaster>
          )}

          {/* Right Column: Details & Management (50% Width) */}
          {selectedDrive ? (
            <DetailsColumn elevation={0}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#ffffff',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}
                    >
                      UPLOADED ON
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      {formatDate(selectedDrive?.start_time)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}
                    >
                      UPLOADED BY
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      {selectedDrive?.inspector_name || 'Admin User'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}
                    >
                      TOTAL DURATION
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4f46e5' }}>
                      {calcDuration(selectedDrive?.start_time, selectedDrive?.end_time)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Management Actions Cards */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                  gap: 1.5,
                }}
              >
                {/* Lane Management Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: '1px solid #e2e8f0',
                    background: 'linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#dcfce7',
                          color: '#166534',
                        }}
                      >
                        <AddRoadOutlinedIcon fontSize="small" />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#166534' }}>
                        Lane
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: '#64748b', lineHeight: 1.4, display: 'block', mb: 2 }}
                    >
                      Add new lane annotations or view existing lane data.
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                    <Tooltip title="Add lane annotations">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/lane-marking/${selectedDrive.id}`)}
                        sx={{
                          bgcolor: '#166534',
                          color: '#fff',
                          '&:hover': { bgcolor: '#14532d' },
                        }}
                        aria-label="Add lane annotations"
                      >
                        <AddRoadOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View lane data">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/lane-map/${selectedDrive.id}`)}
                        sx={{
                          bgcolor: '#ecfdf5',
                          color: '#166534',
                          border: '1px solid #bbf7d0',
                          '&:hover': { bgcolor: '#d1fae5' },
                        }}
                        aria-label="View lane data"
                      >
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>

                {/* Sign Management Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: '1px solid #e2e8f0',
                    background: 'linear-gradient(180deg, #eef2ff 0%, #ffffff 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#e0e7ff',
                          color: '#3730a3',
                        }}
                      >
                        <SettingsOutlinedIcon fontSize="small" />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3730a3' }}>
                        Sign
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: '#64748b', lineHeight: 1.4, display: 'block', mb: 2 }}
                    >
                      Add or review sign annotations for the selected drive.
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                    <Tooltip title="Open sign management">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/dashboard?drive=${selectedDrive.id}`)}
                        sx={{
                          bgcolor: '#3730a3',
                          color: '#fff',
                          '&:hover': { bgcolor: '#312e81' },
                        }}
                        aria-label="Open sign management"
                      >
                        <SettingsOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>

                {/* Rails Management Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: '1px solid #e2e8f0',
                    background: 'linear-gradient(180deg, #fefce8 0%, #ffffff 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#fef3c7',
                          color: '#854d0e',
                        }}
                      >
                        <RouteOutlinedIcon fontSize="small" />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#854d0e' }}>
                        Rails
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: '#64748b', lineHeight: 1.4, display: 'block', mb: 2 }}
                    >
                      Add new rail annotations or review existing rail data.
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                    <Tooltip title="Add rail annotations">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/rails-marking/${selectedDrive.id}`)}
                          sx={{
                            bgcolor: '#f59e0b',
                            color: '#fff',
                            '&:hover': { bgcolor: '#d97706' },
                            opacity: 0.7,
                          }}
                          aria-label="Add rail annotations"
                        >
                          <RouteOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="View rail data">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/rails-map/${selectedDrive.id}`)}
                          sx={{
                            bgcolor: '#fef3c7',
                            color: '#854d0e',
                            border: '1px solid #fcd34d',
                            opacity: 0.7,
                          }}
                          aria-label="View rail data"
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Paper>
              </Box>

              {/* Drive Recording Video Container */}
              <InfoCard title="Drive Recording">
                <Box
                  sx={{
                    height: 320,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8fafc',
                    borderRadius: 2,
                    border: '1px dashed #cbd5e1',
                    overflow: 'hidden',
                  }}
                >
                  {isDriveVideoLoading ? (
                    <CircularProgress size={32} />
                  ) : driveVideoUrl || driveVideo ? (
                    <Box
                      ref={videoRef}
                      src={driveVideoUrl || driveVideo}
                      component="video"
                      controls
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '8px',
                        backgroundColor: '#0f172a',
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                      No video uploaded for this drive
                    </Typography>
                  )}
                </Box>
              </InfoCard>
            </DetailsColumn>
          ) : null}
        </MainGrid>

        {/* Dialogs */}
        <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Edit Drive Name
            </Typography>
            <TextField
              fullWidth
              label="Drive Name"
              value={newDriveName}
              onChange={(e) => setNewDriveName(e.target.value)}
              sx={{ mb: 3 }}
            />
            <Box sx={{ ...flexAlignCenterGap(2), justifyContent: 'flex-end' }}>
              <CancelButton onClick={() => setOpenEdit(false)}>Back</CancelButton>
              <PrimaryButton onClick={handleEditConfirm} disabled={isEditConfirmDisabled}>
                Confirm
              </PrimaryButton>
            </Box>
          </Box>
        </Dialog>

        <Dialog
          open={openReprocess}
          onClose={() => setOpenReprocess(false)}
          fullWidth
          maxWidth="sm"
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Reprocess Drive
            </Typography>
            <Typography sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
              Are you sure you want to reprocess this drive? This may take some time.
            </Typography>
            <Box sx={{ ...flexAlignCenterGap(2), justifyContent: 'flex-end' }}>
              <CancelButton onClick={() => setOpenReprocess(false)}>Cancel</CancelButton>
              <PrimaryButton onClick={handleReprocessConfirm}>Reprocess</PrimaryButton>
            </Box>
          </Box>
        </Dialog>

        <Dialog open={openVisit} onClose={() => setOpenVisit(false)} fullWidth maxWidth="sm">
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Visit Cloud Storage
            </Typography>
            <Typography sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
              This will open the cloud storage URL for this drive in a new tab.
            </Typography>
            <Box sx={{ ...flexAlignCenterGap(2), justifyContent: 'flex-end' }}>
              <CancelButton onClick={() => setOpenVisit(false)}>Cancel</CancelButton>
              <PrimaryButton onClick={handleVisitConfirm}>Open</PrimaryButton>
            </Box>
          </Box>
        </Dialog>

        <ManageCollectionModal
          open={manageCollectionOpen}
          onClose={() => setManageCollectionOpen(false)}
          onUpdate={(msg) => {
            setManageCollectionOpen(false);
            if (msg) {
              setSnackbar({ ...msg, open: true });
            }
            if (currentCollectionId) {
              dispatch(fetchDrivesByCollection(currentCollectionId));
            }
          }}
          collectionId={currentCollectionId}
          collectionName={currentCollectionName}
          companyId={companydata?.id}
        />
      </GridLayout>
    </Wrapper>
  );
};

export default Drive;
